import { useMemo } from 'react';
import { DateRange, FilterData, FilterOptionsMap } from '@bitrise/bitkit';
import { DateTime } from 'luxon';
import useAppBranchesForFilter from '../../hooks/useAppBranchesForFilter';
import useAppWorkflowsForFilter from '../../hooks/useAppWorkflowsForFilter';

type UseBuildFilterData = {
  branches: ReturnType<typeof useAppBranchesForFilter>['data'];
  fetchBranches: ReturnType<typeof useAppBranchesForFilter>['fetchQuery'];
  fetchPipelineables: ReturnType<typeof useAppWorkflowsForFilter>['fetchQuery'];
  pipelines?: string[];
  workflows?: string[];
};

const getWorkflowOptions = (workflows?: string[]) => workflows?.map((workflow) => `workflow-${workflow}`) || [];
const getPipelineOptions = (pipelines?: string[]) => pipelines?.map((pipeline) => `pipeline-${pipeline}`) || [];

const useBuildFilterData = ({
  branches,
  fetchBranches,
  fetchPipelineables,
  pipelines,
  workflows,
}: UseBuildFilterData) => {
  return useMemo(() => {
    const pipelineNames = new Set(pipelines);
    const workflowNames = new Set(workflows);

    const pipelineOptions = getPipelineOptions(pipelines);
    const workflowOptions = getWorkflowOptions(workflows);

    const pipelinableOptionsMap: FilterOptionsMap = {};

    workflows?.forEach((currWorkflow) => {
      pipelinableOptionsMap[`workflow-${currWorkflow}`] =
        `${currWorkflow} ${pipelineNames.has(currWorkflow) ? '(workflow)' : ''}`.trim();
    });

    pipelines?.forEach((currPipeline) => {
      pipelinableOptionsMap[`pipeline-${currPipeline}`] =
        `${currPipeline} ${workflowNames.has(currPipeline) ? '(pipeline)' : ''}`.trim();
    });

    const branchOptionsMap = branches?.map((currBranch) => ({
      [currBranch.name]: currBranch.name,
    }));

    const minSelectableDate = DateTime.now().minus({ days: 200 });

    const filterData: FilterData = {
      branch: {
        categoryNamePlural: 'branches',
        options: branches?.map((currBranch) => currBranch.name),
        optionsMap: branchOptionsMap,
        onAsyncSearch: async (_category, keyword) => {
          const data = await fetchBranches(keyword);
          return {
            options: data.data?.data?.map((currBranch) => currBranch.name),
          };
        },
        type: 'select',
      },
      workflow: {
        categoryName: 'workflow/pipeline',
        categoryNamePlural: 'workflows/pipelines',
        options: [...workflowOptions, ...pipelineOptions],
        optionsMap: pipelinableOptionsMap,
        onAsyncSearch: async (_category, keyword) => {
          const data = await fetchPipelineables(keyword);
          return {
            options: [...getWorkflowOptions(data.workflows), ...getPipelineOptions(data.pipelines)],
          };
        },
        type: 'select',
      },
      status: {
        categoryNamePlural: 'statuses',
        options: ['running', 'succeeded', 'failed', 'aborted', 'succeeded_with_abort', 'on_hold'],
        optionsMap: {
          running: 'Running',
          succeeded: 'Successful',
          failed: 'Failed',
          aborted: 'Aborted',
          succeeded_with_abort: 'Aborted With Success',
          on_hold: 'On Hold',
        },
        type: 'select',
      },
      trigger_event_type: {
        categoryName: 'trigger',
        categoryNamePlural: 'triggers',
        options: ['push', 'pull-request', 'tag'],
        optionsMap: {
          push: 'Pushes',
          'pull-request': 'Pull Requests',
          tag: 'Tags',
        },
        type: 'select',
      },
      artifact_type: {
        categoryName: 'artifact',
        categoryNamePlural: 'artifacts',
        options: ['ios-ipa', 'android-apk'],
        optionsMap: {
          'ios-ipa': 'iOS IPA',
          'android-apk': 'Android APK/AAB',
        },
        type: 'select',
      },
      date_range: {
        categoryName: 'Date',
        categoryNamePlural: 'dates',
        type: 'dateRange',
        isMultiple: true,
        selectable: new DateRange(minSelectableDate, DateTime.now()),
        dayTooltip: ({ date }) => {
          if (date < minSelectableDate) {
            return 'Artifacts can be listed for up to 200 days.';
          }

          if (date > DateTime.now().endOf('day')) {
            return 'Can not select a date in the future.';
          }

          return undefined;
        },
      },
    } as FilterData;

    return filterData;
  }, [branches, fetchBranches, fetchPipelineables, pipelines, workflows]);
};

export default useBuildFilterData;
